/**
 * UI-29 (Levent, 22 Oct 2026): the URGENT banner on the booking page for an online booking
 * the customer has paid for that is not confirmed with the supplier.
 *
 * ONE definition, shared with the backend. This mirrors PAID_NOT_CONFIRMED_SQL in
 * backend/controllers/dashboard/dashboard.controller.js:
 *
 *   b.deletedAt IS NULL
 *   AND b.bookingType = 'Online'
 *   AND b.paymentStatus = 'Paid'
 *   AND b.status NOT IN ('Confirmed', 'Cancelled', 'Draft')
 *
 * The same SQL feeds the header bell (getNotifications in that controller) and
 * GET /api/system/action-required (actionRequired in backend/services/opsIncidents.service.js),
 * so the banner, the dashboard section and the bell always agree. The URGENT email itself is
 * sent by notifyNotConfirmed in backend/website/controllers/onlineBooking.controller.js when a
 * paid confirmation fails; it logs "URGENT alert emailed", and recordPartialConfirmation in the
 * same file logs "Online Booking Partially Confirmed" when part of a package was reserved.
 *
 * Case handling: MySQL compares these columns with a case-insensitive (_ci) collation, so
 * 'online' matches 'Online' there; the comparisons below ignore case (and surrounding spaces)
 * the same way. A missing status is never shown: in SQL, NULL NOT IN (...) is not true.
 * 'API' bookings are not 'Online' and are not shown, exactly as in the SQL.
 */
import { formatDMY, formatDMYTime } from "../../../utils/dateFormat";

export const URGENT_TITLE = "URGENT: Payment received, booking not confirmed";

/** Statuses that end the alert (the SQL's NOT IN list). */
export const URGENT_CLEARED_STATUSES = ["Confirmed", "Cancelled", "Draft"];

/** The activity-log actions the backend writes for this incident, newest shown first. */
export const PARTIAL_CONFIRMED_ACTION = "Online Booking Partially Confirmed";
export const URGENT_EMAILED_ACTION = "URGENT alert emailed";
export const URGENT_LOG_ACTIONS = [PARTIAL_CONFIRMED_ACTION, URGENT_EMAILED_ACTION];

const norm = (v) => String(v ?? "").trim().toLowerCase();

/** True when a booking status keeps the alert open (present and not in the cleared list). */
export const statusKeepsUrgent = (status) => {
  const s = norm(status);
  return s !== "" && !URGENT_CLEARED_STATUSES.some((c) => norm(c) === s);
};

/**
 * PURE. The banner predicate, on the booking as GET /api/bookings/:id returns it.
 * The mapped page shape (origin / bookingStatus) is accepted as a fallback.
 */
export function isPaidNotConfirmed(booking) {
  if (!booking || booking.deletedAt) return false;
  return norm(booking.bookingType ?? booking.origin) === "online"
    && norm(booking.paymentStatus) === "paid"
    && statusKeepsUrgent(booking.status ?? booking.bookingStatus);
}

const SUPPLIER_LABELS = { airtuerk: "Airtürk", airturk: "Airtürk", "airtürk": "Airtürk" };

const parseDetails = (p) => {
  const d = p?.productDetails;
  if (typeof d === "string") {
    try { return JSON.parse(d) || {}; } catch { return {}; }
  }
  return d || {};
};

const supplierLabel = (p, d) => {
  const raw = String(p.supplierName || d.supplier || "").trim();
  if (!raw) return "";
  return SUPPLIER_LABELS[norm(raw)] || raw;
};

const flightRoute = (d) => {
  const legs = Array.isArray(d.legs) ? d.legs : [];
  const from = d.from || legs[0]?.from || legs[0]?.origin || legs[0]?.departureAirport;
  const last = legs[legs.length - 1];
  const to = d.to || last?.to || last?.destination || last?.arrivalAirport;
  return from || to ? `${from || "?"} → ${to || "?"}` : "";
};

const describeProduct = (p, d) => {
  if (p.productType === "Flight") return flightRoute(d);
  if (p.productType === "Hotel") return d.hotelName || d.hotelCode || "";
  return d.name || d.description || "";
};

const dateRange = (p, d) => {
  const start = p.serviceStartDate || d.checkin || d.checkIn || d.depdate;
  const end = p.serviceEndDate || d.checkout || d.checkOut || d.retdate;
  const a = formatDMY(start, "");
  const b = formatDMY(end, "");
  if (a && b && a !== b) return `${a} - ${b}`;
  return a || b;
};

/**
 * PURE. One line per product with its state. A product already reserved with its supplier
 * is Confirmed and carries its supplier reference: staff keep it or cancel it.
 */
export function urgentProductLines(products = []) {
  return (Array.isArray(products) ? products : [])
    .filter((p) => p && !p.deletedAt)
    .map((p) => {
      const d = parseDetails(p);
      const status = String(p.productStatus || "").trim() || "Pending";
      const s = norm(status);
      return {
        id: p.id,
        type: p.productType || "Product",
        supplier: supplierLabel(p, d),
        what: describeProduct(p, d),
        dates: dateRange(p, d),
        status,
        reserved: s === "confirmed",
        cancelled: s === "cancelled",
        reference: String(p.supplierReference || "").trim(),
      };
    });
}

const logTime = (l) => l?.performedAt || l?.createdAt || l?.timestamp || null;
const logText = (l) => String(l?.description ?? l?.note ?? "").trim();

/**
 * PURE. The latest entry of each URGENT_LOG_ACTIONS action, newest first. Accepts the raw
 * activity rows ({ action, description, performedAt }) or the page's mapped audit log
 * ({ action, note, timestamp }). Only what the log holds: nothing is invented.
 */
export function urgentActivityLines(logs = []) {
  const latest = new Map();
  for (const l of Array.isArray(logs) ? logs : []) {
    const action = URGENT_LOG_ACTIONS.find((a) => norm(a) === norm(l?.action));
    if (!action) continue;
    const t = new Date(logTime(l) || 0).getTime() || 0;
    const prev = latest.get(action);
    if (!prev || t > prev.t) latest.set(action, { t, entry: l });
  }
  return [...latest.entries()]
    .sort((a, b) => b[1].t - a[1].t)
    .map(([action, { entry }]) => ({
      id: entry.id ?? action,
      action,
      at: formatDMYTime(logTime(entry), ""),
      text: logText(entry),
    }));
}
